import { CEPresentationContext } from '../components/CEPresentation';
import { useCEWorkshop } from '../components/useCEWorkshop';
import VariaModal, { variaCards, isVaria } from '../components/VariaModal';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Brain,
  CheckCircle2,
  Circle,
  Compass,
  DollarSign,
  FileText,
  Gauge,
  MessageSquare,
  Search,
  Sparkles,
  Tag,
  Target,
  Layers3,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { PreparationList, PreparationToolbar, useLacPreparation } from '../components/LacPreparation';
import Header from '../layout/Header';
import PrismeImmersiveStory from '../components/PrismeImmersiveStory';
import AikubPositionnementModal from '../components/AikubPositionnementModal';
import AcquisitionsUpsaleModal from '../components/AcquisitionsUpsaleModal';
import VentesAntoineModal from '../components/VentesAntoineModal';
import AtlasDonneesCEModal from '../components/AtlasDonneesCEModal';
import ResultatsEntrepriseBZModal from '../components/ResultatsEntrepriseBZModal';
import ActionnairesGouvernanceModal from '../components/ActionnairesGouvernanceModal';
import ValeursCultureModal from '../components/ValeursCultureModal';
import CloudInfrastructureModal from '../components/CloudInfrastructureModal';
import MidiFormationAgentiqueModal from '../components/MidiFormationAgentiqueModal';
import ScopeOffreBZModal from '../components/ScopeOffreBZModal';
import HumanForcesBZModal from '../components/HumanForcesBZModal';
import ThanksBeingLateModal from '../components/ThanksBeingLateModal';
import InternalToolsBZModal from '../components/InternalToolsBZModal';
import ComitesBZModal from '../components/ComitesBZModal';
import AdministrationCEModal from '../components/AdministrationCEModal';
import AgenticClientPlanCEModal from '../components/AgenticClientPlanCEModal';
import AgentiqueRagModal from '../components/AgentiqueRagModal';
import ServiceSacBZModal from '../components/ServiceSacBZModal';
import { useAuth } from '../contexts/AuthContext';
import FinanceFDPresentation from '../components/financeFD/FinanceFDPresentation';
import { financeFDCard, FINANCE_FD_ID } from '../components/financeFD/card';
import { useFinanceFD } from '../components/financeFD/useFinanceFD';
import { useCompanyResults } from '../components/useCompanyResults';
import {
  WorkshopCard,
  WorkshopStatus,
  lacEpauleFamilies,
  lacEpauleParticipants,
  lacEpauleSummary,
} from '../data/lacEpauleSeptembre2026Data';

const allowedEmails = ['dany.bourassa@bzinc.ca', 'dany.bourassa@aikub.ai', 'eric.durand@bzinc.ca', 'francois.delisle@bzinc.ca'];
const statusLabels: Record<WorkshopStatus, string> = {
  'a-discuter': 'À discuter',
  'en-discussion': 'En discussion',
  complete: 'Complété',
};
const importanceStyles = {
  critique: 'border-rose-300 bg-rose-50 text-rose-800',
  élevée: 'border-amber-300 bg-amber-50 text-amber-800',
  moyenne: 'border-sky-300 bg-sky-50 text-sky-800',
};


type PremiumCardFrame = {
  thesis: string;
  explain: string;
  credibility: string;
  danyLine: string;
  visualLabel: string;
  flow: string[];
};

const premiumFrames: Record<string, PremiumCardFrame> = {
  'midi-formation-agentique': {
    thesis: 'Apprendre ensemble à partir de nos succès avec les bots.',
    explain: 'Idée à lancer : un midi par mois, cinq collègues de différents départements, dix minutes chacun dans une heure.',
    credibility: 'Proposition de Dany; pilote du comité IA à désigner et suivi des démos et participants dans PRIsME à prévoir.',
    danyLine: 'Un défi bienveillant, la fierté de présenter et des idées qui circulent. Pas un classement.',
    visualLabel: 'Nos succès en partage',
    flow: ['Un besoin', 'Un bot', 'Une amélioration', 'Un partage']
  },
  'resultats-entreprise-bz': {
    thesis: 'Partir du réel : BZ n’est plus une petite compagnie qui réagit, c’est une entreprise qui a créé de la valeur et qui doit maintenant se gérer avec plus de cockpit.',
    explain: 'La carte ne sert pas à montrer des chiffres pour impressionner. Elle sert à expliquer la trajectoire : croissance, profitabilité, dividendes, valeur, puis le choix 2027 entre encore plus de volume, plus de marge ou plus de robustesse.',
    credibility: 'Les données annuelles de référence de BZ inc. couvrent 2013-2025 et permettent de sortir du “feeling” pour ouvrir une discussion de gestion appuyée sur des repères financiers.',
    danyLine: '“On ne décide pas 2027 sur des anecdotes. On regarde ce que BZ est devenu, puis on choisit consciemment la prochaine courbe.”',
    visualLabel: 'Trajectoire financière',
    flow: ['Constat 2013-2025', 'Lecture marge / valeur', 'Choix 2027', 'Rituel CE']
  },
  'forces-humaines-bz': {
    thesis: 'Le virage IA ne commence pas par les outils. Il commence par les personnes qui vont porter, absorber, challenger et humaniser le changement.',
    explain: 'Chaque personne est présentée par sa force principale, sa contribution au virage et les zones à protéger. Le but est d’aligner le monde sans exposer personne ni transformer les enjeux en jugement.',
    credibility: 'La lecture reprend le principe humain-first demandé par Dany : forces d’abord, transparence ensuite, protection toujours.',
    danyLine: '“L’IA va accélérer BZ seulement si elle respecte les humains qui font déjà tenir BZ debout.”',
    visualLabel: 'Carte des forces',
    flow: ['Forces', 'Enjeux à protéger', 'Contribution IA', 'Accompagnement']
  },
  'comites-bz-raison-etre-rituels': {
    thesis: 'Un comité BZ utile doit produire de la clarté, des décisions et une trace; sinon c’est une réunion de plus.',
    explain: 'Cette carte clarifie pourquoi les comités existent, quels rituels les rendent utiles, qui décide, et comment PRIsME garde la mémoire des décisions.',
    credibility: 'Elle se base sur l’inventaire PRIsME des comités existants et sur les nouveaux besoins : profitabilité clients, IA agentique et comité des directeurs.',
    danyLine: '“Je ne veux pas plus de comités; je veux des comités qui servent à décider, suivre et responsabiliser.”',
    visualLabel: 'Système de gouvernance',
    flow: ['Pourquoi', 'Mandat', 'Rituel', 'Trace PRIsME']
  },
  'tks-being-late-human-first': {
    thesis: 'Comprendre les accélérations et choisir une transformation humaine, avec du temps pour apprendre.',
    explain: 'Distinguer la thèse de Friedman, les recherches organisationnelles sur l’IA et les orientations à discuter au CE.',
    credibility: 'Auteur, conférence primaire, OCDE, NBER et enquête McKinsey : sources consultées avec leurs limites, sans promesse de réussite.',
    danyLine: 'Le récit de lecture et la position stratégique sont réservés au CE dans la fenêtre détaillée.',
    visualLabel: 'Apprendre à se transformer',
    flow: ['Comprendre', 'Expérimenter', 'Observer', 'Ajuster']
  },
  'prisme-master': {
    thesis: 'PRIsME doit devenir le système nerveux de BZ : pas un site de pages, mais un endroit où l’organisation comprend, décide et garde ses traces.',
    explain: 'La carte explique ce qui devient officiel dans PRIsME, ce qui reste dans TactiCE/AIKUB/Drive, et comment éviter une collection d’outils sans doctrine.',
    credibility: 'Le contenu s’appuie sur les surfaces déjà présentes : CE, IA Agentic, Atlas des données, comités, projets et gestion documentaire.',
    danyLine: '“PRIsME doit aider les gens à savoir où on est rendu, pas ajouter un autre endroit à vérifier.”',
    visualLabel: 'Cockpit BZ',
    flow: ['Source de vérité', 'Décisions', 'Accès', 'Suivi']
  },
  'atlas-donnees-bz': {
    thesis: 'Du catalogue des actifs à l’Atlas des données BZ et clients : l’humain garde le contrôle.',
    explain: 'Voir les actifs, comprendre les données, nommer les propriétaires et construire des accès granulaires approuvés, traçables et révocables.',
    credibility: 'Catalogue client alimenté par SERVO et Atlas frontend existants; portails de gouvernance à construire, sans droits opérationnels promis.',
    danyLine: 'Créons notre succès, le reste suivra.',
    visualLabel: 'Données & humains',
    flow: ['Besoin client', 'Domaines', 'Propriétaires', 'Accès gouvernés']
  },
  'ia-agentique-rag': {
    thesis: 'Un besoin humain, des sources autorisées, une action contrôlée et un résultat vérifié.',
    explain: 'Distinguer modèle, RAG, agent et workflow; relier exécution hétérogène, gouvernance et adoption des métiers.',
    credibility: 'Sources officielles NIST, Hermes, Qwen, OpenAI, Anthropic et OWASP; inspection locale PRIsME Master / TBA, sans prétendre auditer la production.',
    danyLine: 'Orientation à discuter : accompagner les personnes et prouver les contrôles avant d’élargir.',
    visualLabel: 'Chaîne agentique',
    flow: ['Irritant', 'Source', 'Agent', 'Contrôle humain']
  },
  'aikub-positionnement': {
    thesis: 'Agentique AIKUB inc. : une société distincte spécialisée en agentique IA, créée en 2026.',
    explain: 'AIKUB BOTOPS centralise télémétrie, traçabilité, documentation et gouvernance des bots clients, indépendamment du logiciel agentique.',
    credibility: 'Le différenciateur est humain : compréhension des besoins, onboarding et ajustements terrain Owner ↔ pilote.',
    danyLine: 'Installer des agents est facile. Perdre le contrôle aussi.',
    visualLabel: 'Modèle AIKUB',
    flow: ['Besoin humain', 'BOTOPS', 'Gouvernance', 'Évolution TOP 3']
  },
  'departement-ventes': {
    thesis: 'Comprendre le besoin, vendre un accompagnement et apprendre ensemble.',
    explain: '2026 transforme les ventes. Pas de template d’ODS copié-collé : des choix personnalisés, des guides et un soutien terrain.',
    credibility: 'Cadre client BZ : six piliers et deux transversales; référentiel directeur de compte et guides ODS existants. NIST CSF 2.0 pour la cyber.',
    danyLine: 'La volonté d’apprendre et le réflexe de demander de l’aide font partie de la responsabilité.',
    visualLabel: 'Besoin → accompagnement',
    flow: ['Comprendre', 'Construire les choix', 'Antoine ET directeur concerné', 'Présenter après approbations']
  },
  'cloud-infra': {
    thesis: 'Deux datacenters, une réplication croisée et une transition COLOGIX déjà en cours.',
    explain: 'Distinguer les VM de production et leurs répliques, la relève réseau BGP et la reprise des VM.',
    credibility: 'Portrait déclaré par Dany en septembre 2026 : infrastructure, migrations et conclusion de l’audit SPLA.',
    danyLine: 'Reconnaître le travail de l’équipe et suivre les prochains jalons du cloud BZ.',
    visualLabel: 'Cloud BZ · continuité et transition',
    flow: ['BZ ⇄ Serko', 'Résilience réseau', 'COLOGIX Montréal', 'Domaines, DNS et SPLA']
  },
  'service-ti-sac': {
    thesis: 'L’humain au cœur du service, aujourd’hui et demain.',
    explain: 'Une présentation dédiée à l’équipe, à la relation client et aux prochaines étapes collectives.',
    credibility: 'Le portrait de direction est consultable après autorisation du Comité CE.',
    danyLine: 'Reconnaître les forces et préparer la suite, ensemble.',
    visualLabel: 'Service TI & SAC BZ',
    flow: ['Équipe', 'Relation client', 'Accompagnement', 'Avenir']
  },
  'admin-finance-rh': {
    thesis: 'Une équipe solide et un SAC administratif utile aux équipes internes comme aux clients.',
    explain: 'Transition, complémentarité des forces, parcours de demande et feuille de route 2027 : des petits pas à valider, pas un nouvel organigramme.',
    credibility: 'Vision départementale de Dany, profils CE et périmètres documentés des outils; dossiers financiers et questions existantes conservés.',
    danyLine: 'Stabiliser la continuité, simplifier les pratiques, puis élargir selon la capacité.',
    visualLabel: 'Vision du département · 2026 → 2027',
    flow: ['Transition', 'Équipe', 'SAC administratif', 'Cap 2027']
  },
  'actionnariat-gouvernance': {
    thesis: 'Qui détient BZ, ce que BZ détient et qui porte les responsabilités exécutives.',
    explain: 'Une lecture du capital, l’organigramme de Gouvernance CE avec ses liens et ses fiches, et une vision distincte de refonte administration, finances et RH.',
    credibility: 'Capital : tableau à jour fourni par Dany. Participations de BZ : relevé Touski daté, avec confirmation AIKUB distincte.',
    danyLine: 'Distinguer la détention du capital des responsabilités de direction.',
    visualLabel: 'Écosystème BZ',
    flow: ['Actionnaires', 'BZ inc.', 'Participations', 'Vue exécutive']
  },
  'acquisitions-xelys-tnt': {
    thesis: 'Acquisitions et upsale client : une réflexion qui évolue, pas un pivot décidé.',
    explain: 'Après six mois davantage consacrés aux acquisitions, l’upsale client prend plus de place dans la réflexion. PRIsME, AIKUB et la refonte des ventes ouvrent de nouvelles possibilités au-delà des TI.',
    credibility: 'Retour d’expérience de Dany : plusieurs entreprises évaluées, deux LOI faites et offres encore actives, mais peu de conviction d’un closing probable. Les acquisitions ne sont pas abandonnées.',
    danyLine: 'Ouvrons nos esprits et voyons où cela nous mène.',
    visualLabel: 'Orientation ouverte',
    flow: ['Confiance', 'Écoute terrain', 'Besoin réel', 'Valeur', 'Évolution']
  },
  'valeurs-culture': {
    thesis: 'Depuis 2001, les mêmes quatre valeurs BZ : les réappliquer et les rendre vivantes, pas les remplacer.',
    explain: 'TRANSPARENCE, ÉCOUTE, INNOVATION, DISPONIBILITÉ : vérité avec discernement, besoins réels, volonté d’apprendre et présence aux clients comme aux collègues.',
    credibility: 'Vision de Dany pour cette carte : une organisation en santé respecte ses valeurs et remet l’humain au premier plan.',
    danyLine: 'Croissance oui, jamais au détriment de l’humain.',
    visualLabel: 'Notre ADN · depuis 2001',
    flow: ['TRANSPARENCE', 'ÉCOUTE', 'INNOVATION', 'DISPONIBILITÉ']
  },
  'scope-offre-bz': {
    thesis: 'BZ doit rendre son offre lisible : Cyber, Cloud, SA et IA doivent s’additionner dans une promesse simple, pas se compétitionner.',
    explain: 'La carte sert à choisir ce qu’on vend, ce qu’on arrête, ce qui devient premium/récurrent et comment AIKUB s’intègre sans brouiller le marché.',
    credibility: 'Elle s’appuie sur PRIsME Solutions d’affaires, cybersécurité, offres, IA Agentic et les propositions récentes.',
    danyLine: '“Une offre forte, c’est quand le client comprend vite le problème, la valeur et la suite.”',
    visualLabel: 'Architecture d’offre',
    flow: ['Problème client', 'Package', 'Preuve', 'Livraison']
  },
  'bz-telecom-cyberops-pmbot': {
    thesis: 'Spécialiser pour innover; converger pour garder une vision commune.',
    explain: 'Chaque outil répond à un besoin : comprendre sa raison d’être, ses utilisateurs et ses frontières avant de décider où intégrer.',
    credibility: 'Documentation existante, déclarations de l’atelier et cibles proposées sont distinguées dans l’espace CE.',
    danyLine: 'Créer du changement, capitaliser les succès et centraliser où c’est utile : une direction, pas une fusion déjà réalisée.',
    visualLabel: 'Outils Internes BZ',
    flow: ['Raison d’être', 'Utilisateurs', 'Responsabilités', 'Convergence']
  }
};

function getPremiumFrame(card: WorkshopCard): PremiumCardFrame {
  return premiumFrames[card.id] ?? {
    thesis: card.summary,
    explain: card.whyNow,
    credibility: card.evidence[0] ?? 'Source PRIsME Master et contexte opérationnel BZ.',
    danyLine: '“On veut que tout le monde comprenne où on est rendu et pourquoi ce sujet compte.”',
    visualLabel: 'Lecture stratégique',
    flow: ['Contexte', 'Preuves', 'Discussion', 'Décision']
  };
}

function toneGradient(importance: WorkshopCard['importance']) {
  if (importance === 'critique') return 'from-slate-950 via-indigo-950 to-rose-950';
  if (importance === 'élevée') return 'from-slate-950 via-indigo-950 to-amber-900';
  return 'from-slate-950 via-sky-950 to-teal-900';
}

function SignalBars({ card }: { card: WorkshopCard }) {
  const frame = getPremiumFrame(card);
  const signals = card.signals.slice(0, 4);
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-500">Visuel</p>
          <h3 className="text-lg font-black text-slate-950">{frame.visualLabel}</h3>
        </div>
        <Gauge className="h-6 w-6 text-indigo-500" />
      </div>
      <div className="space-y-3">
        {(signals.length ? signals : card.tags.slice(0, 4)).map((signal, index) => (
          <div key={`${card.id}-signal-${index}`}>
            <div className="mb-1 flex items-center justify-between gap-3 text-xs font-bold text-slate-500">
              <span className="line-clamp-1">{signal}</span>
              <span>{Math.max(35, 92 - index * 14)}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400" style={{ width: `${Math.max(35, 92 - index * 14)}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-4 gap-2">
        {frame.flow.map((step, index) => (
          <div key={step} className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-center">
            <p className="mx-auto mb-2 flex h-7 w-7 items-center justify-center rounded-full bg-slate-950 text-xs font-black text-white">{index + 1}</p>
            <p className="text-[11px] font-black leading-tight text-slate-700">{step}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PremiumNarrative({ card }: { card: WorkshopCard }) {
  const frame = getPremiumFrame(card);
  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-start gap-4">
        <div className="rounded-2xl bg-slate-950 p-3 text-white"><Compass className="h-6 w-6" /></div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Angle de présentation</p>
          <h3 className="mt-1 text-2xl font-black leading-tight text-slate-950">{frame.thesis}</h3>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-indigo-100 bg-indigo-50 p-5">
          <p className="mb-2 flex items-center gap-2 text-sm font-black text-indigo-950"><Brain className="h-4 w-4" /> Ce qu’on explique</p>
          <p className="text-sm leading-relaxed text-indigo-950">{frame.explain}</p>
        </div>
        <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5">
          <p className="mb-2 flex items-center gap-2 text-sm font-black text-emerald-950"><FileText className="h-4 w-4" /> Ce qui donne de la crédibilité</p>
          <p className="text-sm leading-relaxed text-emerald-950">{frame.credibility}</p>
        </div>
      </div>
      <div className="mt-4 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-600">Phrase Dany pour animer</p>
        <p className="mt-2 text-xl font-black leading-snug text-slate-950">{frame.danyLine}</p>
      </div>
    </section>
  );
}

function formatCurrencyCompact(value: number) {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toLocaleString('fr-CA', { maximumFractionDigits: 1 })} M$`;
  if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000).toLocaleString('fr-CA')} k$`;
  return `${value.toLocaleString('fr-CA')} $`;
}

function ResultMetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
      <p className="text-xs font-black uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-black text-emerald-800">{value}</p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{detail}</p>
    </div>
  );
}

function ParticipantAvatar({ participantId, small = false }: { participantId: string; small?: boolean }) {
  const participant = lacEpauleParticipants.find((person) => person.id === participantId);
  if (!participant) return null;
  const size = small ? 'h-7 w-7 text-[10px]' : 'h-10 w-10 text-xs';
  return participant.avatar ? (
    <img
      src={participant.avatar}
      alt={participant.name}
      title={`${participant.name} — ${participant.role}`}
      className={`${size} rounded-full border-2 border-white object-cover shadow-sm`}
    />
  ) : (
    <div
      title={`${participant.name} — ${participant.role}`}
      className={`${size} flex items-center justify-center rounded-full border-2 border-white bg-slate-900 font-black text-white shadow-sm`}
    >
      {participant.initials}
    </div>
  );
}

function CardStatusPill({ status }: { status: WorkshopStatus }) {
  const styles =
    status === 'complete'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : status === 'en-discussion'
        ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${styles}`}>{statusLabels[status]}</span>;
}

function WorkshopCardTile({
  card,
  status,
  onOpen,
}: {
  card: WorkshopCard;
  status: WorkshopStatus;
  onOpen: (card: WorkshopCard) => void;
}) {
  const frame = getPremiumFrame(card);
  // Stable per subject, including VARIA whose legacy coordinates are all zero.
  const deal = Array.from(card.id).reduce((seed, char) => (seed * 31 + char.charCodeAt(0)) >>> 0, 7);
  const tilt = [-8, 6, -4, 9, -6, 4, 7, -9][deal % 8];
  return (
    <button
      type="button"
      data-wall-card={card.id}
      data-deal-angle={tilt}
      onClick={() => onOpen(card)}
      className={`ce-dealt-card group overflow-hidden rounded-[1.9rem] border bg-white text-left shadow-[0_24px_70px_rgba(15,23,42,0.18)] hover:shadow-[0_32px_90px_rgba(15,23,42,0.26)] ${
        status === 'complete' ? 'border-emerald-200 opacity-80' : 'border-white/80'
      }`}
      style={{ '--deal-angle': `${tilt}deg`, '--deal-mobile-angle': `${tilt / 2}deg`, '--deal-drop': `${(deal % 4) * 24}px`, '--deal-slide': `${(deal % 3 - 1) * 9}px` } as CSSProperties}
    >
      <div className={`h-2 bg-gradient-to-r ${toneGradient(card.importance)}`} />
      <div className="p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide ${importanceStyles[card.importance]}`}>
            {card.importance}
          </span>
          <CardStatusPill status={status} />
        </div>
        <h3 className="mb-2 text-lg font-black leading-tight text-slate-950 group-hover:text-indigo-700">{card.title}</h3>
        <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-slate-600">{frame.thesis}</p>
        <div className="mb-4 rounded-2xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">À expliquer</p>
          <p className="mt-1 line-clamp-2 text-xs font-bold leading-snug text-slate-700">{frame.visualLabel} · {frame.flow.join(' → ')}</p>
        </div>
        <div className="mb-4 flex -space-x-2">
          {card.ownerIds.slice(0, 5).map((id) => (
            <ParticipantAvatar key={id} participantId={id} small />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {card.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}
function SubjectModal({
  card,
  status,
  onClose,
  onStatusChange,
  onAtelier,
}: {
  card: WorkshopCard | null;
  status: WorkshopStatus;
  onClose: () => void;
  onStatusChange: (status: WorkshopStatus) => void;
  onAtelier: () => void;
}) {
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  useEffect(() => {
    setActiveTabId(card?.tabs?.[0]?.id ?? null);
  }, [card?.id, card?.tabs]);

  if (!card) return null;
  if (card.id === 'admin-finance-rh') return <AdministrationCEModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'comites-bz-raison-etre-rituels') return <ComitesBZModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'plan-action-agentic-ai-client-bz') return <AgenticClientPlanCEModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'scope-offre-bz') return <ScopeOffreBZModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'forces-humaines-bz') return <HumanForcesBZModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'bz-telecom-cyberops-pmbot') return <InternalToolsBZModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'tks-being-late-human-first') return <ThanksBeingLateModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'ia-agentique-rag') return <AgentiqueRagModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'service-ti-sac') return <ServiceSacBZModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'actionnariat-gouvernance') return <ActionnairesGouvernanceModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'departement-ventes') return <VentesAntoineModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'resultats-entreprise-bz') return <ResultatsEntrepriseBZModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'atlas-donnees-bz') return <AtlasDonneesCEModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'valeurs-culture') return <ValeursCultureModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'cloud-infra') return <CloudInfrastructureModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'midi-formation-agentique') return <MidiFormationAgentiqueModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'aikub-positionnement') return <AikubPositionnementModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  if (card.id === 'acquisitions-xelys-tnt') return <AcquisitionsUpsaleModal card={card} status={status} onClose={onClose} onStatusChange={onStatusChange} onAtelier={onAtelier} />;
  const activeTab = card.tabs?.find((tab) => tab.id === activeTabId) ?? card.tabs?.[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="h-[94vh] w-full max-w-7xl overflow-hidden rounded-[2.2rem] bg-white shadow-2xl"
      >
        <div className={`flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-br ${toneGradient(card.importance)} p-7 text-white`}>
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-wide">{card.family}</span>
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-wide">Importance {card.importance}</span>
              <CardStatusPill status={status} />
            </div>
            <h2 className="text-3xl font-black tracking-tight">{card.title}</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-200">{card.summary}</p>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(94vh-174px)] overflow-y-auto bg-slate-50 p-7">
          <div className="mb-6 grid gap-4 md:grid-cols-3">
            <button onClick={() => onStatusChange('a-discuter')} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left hover:bg-slate-100">
              <Circle className="mb-2 h-5 w-5 text-slate-500" />
              <p className="font-bold text-slate-900">À discuter</p>
            </button>
            <button onClick={() => onStatusChange('en-discussion')} className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-left hover:bg-indigo-100">
              <MessageSquare className="mb-2 h-5 w-5 text-indigo-600" />
              <p className="font-bold text-indigo-900">En discussion</p>
            </button>
            <button onClick={() => onStatusChange('complete')} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-left hover:bg-emerald-100">
              <CheckCircle2 className="mb-2 h-5 w-5 text-emerald-600" />
              <p className="font-bold text-emerald-900">Complété</p>
            </button>
          </div>

          <PremiumNarrative card={card} />

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_380px]">
            <div className="space-y-5">
              <div className="grid gap-5 lg:grid-cols-2">
                <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-2 flex items-center gap-2 text-lg font-black text-slate-950"><Target className="h-5 w-5 text-indigo-600" /> Pourquoi maintenant</h3>
                  <p className="text-sm leading-relaxed text-slate-700">{card.whyNow}</p>
                </section>
                <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-3 flex items-center gap-2 text-lg font-black text-slate-950"><Layers3 className="h-5 w-5 text-amber-500" /> Matière à expliquer</h3>
                  <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
                    {card.enrichedContext.map((item) => <li key={item} className="flex gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />{item}</li>)}
                  </ul>
                </section>
              </div>
              {card.companyResults ? (
                <section className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-5 shadow-sm">
                  <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-600">Données annuelles · {card.companyResults.period}</p>
                      <h3 className="mt-1 flex items-center gap-2 text-lg font-black text-slate-950"><BarChart3 className="h-5 w-5 text-emerald-600" /> Résultats financiers BZ inc.</h3>
                      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{card.companyResults.headline}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-right shadow-sm">
                      <p className="text-xs font-black uppercase tracking-wide text-slate-500">Source</p>
                      <p className="text-sm font-black text-slate-900">{card.companyResults.sourceCompany}</p>
                    </div>
                  </div>

                  <div className="mb-5 grid gap-3 md:grid-cols-5">
                    <ResultMetricCard label="Revenus" value={card.companyResults.totals.revenueGrowth} detail="Croissance 2013-2025" />
                    <ResultMetricCard label="Profit" value={card.companyResults.totals.profitGrowth} detail="Croissance 2013-2025" />
                    <ResultMetricCard label="Valeur" value={card.companyResults.totals.valueGrowth} detail="Croissance 2013-2025" />
                    <ResultMetricCard label="Dividendes" value={card.companyResults.totals.dividendsPaid} detail="Cumul des montants suivis" />
                    <ResultMetricCard label="Marge moy." value={card.companyResults.totals.avgMargin} detail="Profit / revenus cumulés" />
                  </div>

                  <div className="grid gap-5 xl:grid-cols-[1fr_280px]">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <div className="grid grid-cols-[72px_1fr_1fr_1fr_1fr_76px] gap-2 bg-slate-950 px-4 py-3 text-[11px] font-black uppercase tracking-wide text-white">
                        <span>Année</span>
                        <span>Revenus</span>
                        <span>Profit</span>
                        <span>Divid.</span>
                        <span>Valeur</span>
                        <span>Marge</span>
                      </div>
                      <div className="max-h-[360px] overflow-y-auto">
                        {card.companyResults.years.map((year) => (
                          <div key={year.year} className="grid grid-cols-[72px_1fr_1fr_1fr_1fr_76px] gap-2 border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
                            <span className="font-black text-slate-950">{year.year}</span>
                            <span>{formatCurrencyCompact(year.revenue)}</span>
                            <span className="font-bold text-emerald-700">{formatCurrencyCompact(year.profit)}</span>
                            <span>{formatCurrencyCompact(year.dividends)}</span>
                            <span className="font-bold text-indigo-700">{formatCurrencyCompact(year.value)}</span>
                            <span>{year.margin.toLocaleString('fr-CA', { maximumFractionDigits: 1 })} %</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-600 p-5 text-white shadow-sm">
                        <TrendingUp className="mb-3 h-8 w-8" />
                        <p className="text-sm font-black uppercase tracking-wide text-emerald-100">Lecture clé</p>
                        <p className="mt-2 text-xl font-black leading-tight">Le profit et la valeur ont grandi plus vite que les revenus.</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <DollarSign className="mb-3 h-7 w-7 text-amber-500" />
                        <p className="text-sm font-black text-slate-950">Notes originales</p>
                        <div className="mt-3 space-y-2 text-xs leading-relaxed text-slate-600">
                          {card.companyResults.years.slice(-4).map((year) => (
                            <p key={year.year} className="rounded-xl bg-slate-50 px-3 py-2"><strong>{year.year}</strong> — {year.note}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}
              {card.tabs?.length ? (
                <section className="rounded-3xl border border-indigo-200 bg-white p-5 shadow-sm">
                  <div className="mb-4">
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-500">Carte premium · matière de présentation</p>
                    <h3 className="mt-1 text-lg font-black text-slate-950">
                      Lecture stratégique enrichie
                    </h3>
                  </div>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {card.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTabId(tab.id)}
                        className={`rounded-full border px-3 py-2 text-xs font-black transition ${
                          activeTab?.id === tab.id
                            ? 'border-indigo-600 bg-indigo-600 text-white shadow-md'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-indigo-200 hover:bg-indigo-50'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  {activeTab ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <h4 className="mb-3 text-base font-black text-slate-950">{activeTab.title}</h4>
                      <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
                        {activeTab.points.map((point) => (
                          <li key={point} className="flex gap-2">
                            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </section>
              ) : null}
              <section className="rounded-3xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-lg font-black text-slate-950">Questions pour faire parler le groupe</h3>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
                  {card.questions.map((item) => <li key={item} className="rounded-2xl bg-slate-50 px-4 py-3">{item}</li>)}
                </ul>
              </section>
            </div>
            <div className="space-y-5">
              <SignalBars card={card} />
              <section className="rounded-3xl border border-indigo-200 bg-indigo-50 p-5">
                <h3 className="mb-3 text-lg font-black text-indigo-950">Décisions à obtenir</h3>
                <ul className="space-y-2 text-sm leading-relaxed text-indigo-900">
                  {card.decisions.map((item) => <li key={item} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{item}</li>)}
                </ul>
              </section>
              <section className="rounded-3xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-lg font-black text-slate-950">Participants naturels</h3>
                <div className="space-y-3">
                  {card.ownerIds.map((id) => {
                    const participant = lacEpauleParticipants.find((person) => person.id === id);
                    if (!participant) return null;
                    return <div key={id} className="flex items-center gap-3"><ParticipantAvatar participantId={id} /><div><p className="text-sm font-bold text-slate-900">{participant.name}</p><p className="text-xs text-slate-500">{participant.role}</p></div></div>;
                  })}
                </div>
              </section>
              <section className="rounded-3xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-lg font-black text-slate-950">Sources / preuves à citer</h3>
                <ul className="space-y-2 text-xs leading-relaxed text-slate-600">
                  {card.evidence.map((item) => <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>)}
                </ul>
              </section>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap justify-between gap-3 border-t border-slate-200 pt-5">

            <button onClick={onClose} className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">Retour au mur de cartes</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function LacEpauleSeptembre2026() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('Tous');
  const [selectedImportance, setSelectedImportance] = useState('Toutes');
  const [selectedCardState, setSelectedCard] = useState<WorkshopCard | null>(null);
  const meeting = useCEWorkshop(!!user?.email && allowedEmails.includes(user.email.toLowerCase()));
  const [statusFilter, setStatusFilter] = useState('Toutes');
  const [shuffling, setShuffling] = useState(false);
  const shuffleLock = useRef(false);
  const shuffleTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(shuffleTimer.current), []);
  const localPreparation = useLacPreparation();
  const finance = useFinanceFD(user?.email);
  const results = useCompanyResults(user?.email);
  const selectedCard = selectedCardState?.id === 'resultats-entreprise-bz' ? results.payload : selectedCardState;
  const { document } = localPreparation;
  // Only a fresh authorized response permits materializing even cached preparation.
  const cards = useMemo(() => {
    const visible = localPreparation.cards.filter(card => card.id !== 'resultats-entreprise-bz');
    if (results.payload) visible.unshift(results.payload);
    if (finance.payload) visible.push({ ...financeFDCard, ...document.preparation.entries[FINANCE_FD_ID]?.content });
    return [...visible, ...variaCards];
  }, [localPreparation.cards, document.preparation.entries, finance.payload, results.payload]);
  const statuses = Object.fromEntries(Object.entries(meeting.state?.cards ?? {}).map(([id, entry]) => [id, entry.status]));
  const preparation = { ...localPreparation, cards, sharedDiscussion: statuses };
  const pendingPause = meeting.state?.pending[0];
  const legacyMissing = cards.filter(card => !card.id.startsWith('local-') && document.discussion[card.id] && meeting.state && !meeting.state.cards[card.id]);

  useEffect(() => {
    if (!user?.email || !allowedEmails.includes(user.email.toLowerCase())) navigate('/comites');
  }, [user, navigate]);

  const filteredCards = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter((card) => {
      const matchesQuery = !q || [card.title, card.summary, card.family, ...card.tags, ...card.ownerIds.map(id => lacEpauleParticipants.find(person => person.id === id)?.name ?? id)].join(' ').toLowerCase().includes(q);
      const matchesFamily = selectedFamily === 'Tous' || card.family === selectedFamily;
      const matchesImportance = selectedImportance === 'Toutes' || card.importance === selectedImportance;
      return matchesQuery && matchesFamily && matchesImportance && (statusFilter === 'Toutes' || (statusFilter === 'Complétées' ? statuses[card.id] === 'complete' : statuses[card.id] !== 'complete'));
    });
  }, [cards, query, selectedFamily, selectedImportance, statusFilter, meeting.state]);

  const completedCount = cards.filter((card) => !isVaria(card.id) && statuses[card.id] === 'complete').length;
  const criticalCount = cards.filter((card) => card.importance === 'critique').length;

  if (!user?.email || !allowedEmails.includes(user.email.toLowerCase())) return null;

  const updateStatus = async (cardId: string, status: WorkshopStatus) => {
    const ok=await meeting.mutate(cardId,{status});
    if(ok && status==='complete') {
      const next=meeting.takeTriggeredPause();
      const pause=variaCards.find(card=>card.id===next);
      if(pause)setSelectedCard(pause);
    }
    return ok;
  };
  const eligible = filteredCards.filter(card => !isVaria(card.id) && statuses[card.id] !== 'complete' && !card.id.startsWith('local-'));
  const chooseRandomCard = () => {
    if (shuffleLock.current || !eligible.length || !meeting.state || meeting.saving) return;
    shuffleLock.current = true; setShuffling(true);
    shuffleTimer.current = setTimeout(async () => {
      try {
        const fresh = await meeting.refresh();
        const pool = eligible.filter(card => fresh.cards[card.id]?.status !== 'complete');
        if (pool.length) setSelectedCard(pool[Math.floor(Math.random() * pool.length)]);
      } catch { /* The shared-state alert remains the source of truth; no stale draw. */ }
      finally { shuffleLock.current = false; setShuffling(false); }
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1000);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Header title="Lac à l’épaule — Septembre 2026" subtitle="Atelier stratégique BZ en cartes libres : choisir le prochain sujet naturellement, documenter la discussion et compléter au fil de l’échange." />
      <main className="px-6 pb-10 xl:px-10">
        <button onClick={() => navigate('/comite-ce')} className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50">
          <ArrowLeft className="h-4 w-4" /> Retour Comité CE
        </button>

        <section className="mb-6 overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 p-7 text-white shadow-2xl">
          <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr] lg:items-end">
            <div>
              <p className="mb-3 text-sm font-black uppercase tracking-[0.22em] text-indigo-300">{lacEpauleSummary.dateLabel} · Version atelier</p>
              <h1 className="max-w-4xl text-4xl font-black tracking-tight md:text-5xl">Moins ordre du jour. Plus conversations qui font avancer BZ.</h1>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-200">Chaque sujet devient une carte de discussion : importance, tags, participants naturels, contexte enrichi, questions puissantes et décisions attendues. L’ordre importe moins que la qualité des décisions.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4"><p className="text-xs text-slate-300">Cartes · sujets + pauses</p><p className="text-3xl font-black">{cards.length}</p></div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4"><p className="text-xs text-slate-300">Critiques</p><p className="text-3xl font-black">{criticalCount}</p></div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4"><p className="text-xs text-slate-300">Sujets complétés</p><p className="text-3xl font-black">{completedCount}</p></div>
            </div>
          </div>
        </section>

        <section className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto_auto_auto] lg:items-center">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un sujet, tag, participant..." className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm font-medium outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
            </div>
            <select value={selectedFamily} onChange={(event) => setSelectedFamily(event.target.value)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700">
              <option>Tous</option>
              {Array.from(new Set([...lacEpauleFamilies, ...cards.map(card => card.family)])).map((family) => <option key={family}>{family}</option>)}
            </select>
            <select value={selectedImportance} onChange={(event) => setSelectedImportance(event.target.value)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700">
              <option>Toutes</option>
              <option>critique</option>
              <option>élevée</option>
              <option>moyenne</option>
            </select>
            <div className="lg:col-span-4"><button onClick={chooseRandomCard} disabled={shuffling || !eligible.length || !meeting.state || meeting.saving} aria-busy={shuffling} className="ce-surprise inline-flex items-center justify-center gap-4 rounded-2xl px-5 py-5 font-black text-white shadow-lg disabled:opacity-50"><Sparkles className="h-8 w-8" />{shuffling ? 'On brasse les cartes…' : 'Carte surprise'}</button><p className="mt-2 text-sm text-slate-600" role="status">{eligible.length} sujets à discuter dans les filtres actifs · VARIA exclues. {eligible.length === 0 && 'Aucun sujet à tirer.'}</p></div>
          </div>
        </section>

        <section className="mb-5 rounded-2xl border border-indigo-200 bg-white p-4" aria-label="État partagé de la rencontre">
          <div className="flex flex-wrap gap-2">{['À discuter','Complétées','Toutes'].map(label => <button key={label} aria-pressed={statusFilter === label} onClick={()=>setStatusFilter(label)} className={`rounded-xl border px-4 py-2 font-bold ${statusFilter===label?'bg-indigo-700 text-white':'bg-white text-indigo-900'}`}>{label}</button>)}</div>
          <p className="mt-3 text-sm">{completedCount} sujets complétés · {meeting.state?.completedOnce.length ?? 0} sujets uniques atteints · une pause proposée aux paliers 3, 6, 9, 12, 15 et 18.</p>
          <p role={meeting.error?'alert':'status'} className="mt-2 text-sm">{meeting.error || meeting.message || (meeting.state ? 'Statuts et notes de discussion partagés avec le CE.' : 'Lecture du serveur…')}</p>
          {meeting.error && <button onClick={()=>void meeting.refresh().catch(()=>{})} className="mt-2 underline">Réessayer la lecture</button>}
          {pendingPause && <div className="mt-3 rounded-xl bg-amber-50 p-3"><strong>Pause {pendingPause.split('-')[1]} · VARIA et Questions proposée</strong><p>Ouvrir une pause ne la marque pas complétée.</p><div className="mt-2 flex flex-wrap gap-3"><button onClick={()=>setSelectedCard(variaCards.find(c=>c.id===pendingPause)!)} className="font-bold underline">Ouvrir la pause</button><button disabled={meeting.saving} onClick={()=>void meeting.mutate(pendingPause,{acknowledge:true})} className="underline">Poursuivre sans rappel</button></div></div>}
        </section>
        {!!legacyMissing.length && <button disabled={meeting.saving} className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" onClick={()=>{if(window.confirm('Importer seulement les anciens statuts absents? Aucun état serveur ne sera remplacé. Les complétions historiques compteront dans les paliers; les notes de préparation restent dans leur sauvegarde locale.'))void meeting.importLegacy(legacyMissing.map(card=>({id:card.id,status:document.discussion[card.id]})));}}>Importer {legacyMissing.length} anciens statuts absents du serveur</button>}
        <PreparationToolbar store={preparation} />
        {finance.error && <p role="status" className="mb-4 text-sm text-slate-600">{finance.error} <button className="underline" onClick={() => void finance.refresh()}>Réessayer la consultation privée</button></p>}
        {document.view === 'list' ? <PreparationList store={preparation} cards={filteredCards} onOpen={setSelectedCard} /> : <section className="grid gap-6 xl:grid-cols-[1fr_340px]">
          <div className="ce-wall relative min-h-[940px] overflow-hidden rounded-[2.2rem] border border-slate-200 bg-[radial-gradient(circle_at_12%_18%,rgba(99,102,241,0.18),transparent_26%),radial-gradient(circle_at_82%_28%,rgba(20,184,166,0.16),transparent_22%),radial-gradient(circle_at_50%_90%,rgba(251,191,36,0.14),transparent_24%),#f8fafc] shadow-xl">
            <div className="absolute left-6 top-6 rounded-full border border-slate-200 bg-white/85 px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-500 backdrop-blur">Mur de cartes · {filteredCards.length} visibles</div>
            {filteredCards.map((card) => <WorkshopCardTile key={card.id} card={card} status={statuses[card.id] || 'a-discuter'} onOpen={setSelectedCard} />)}
          </div>

          <aside className="space-y-5">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-slate-950"><Users className="h-5 w-5 text-indigo-600" /> Participants</h2>
              <div className="space-y-3">
                {lacEpauleParticipants.map((person) => (
                  <div key={person.id} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
                    <ParticipantAvatar participantId={person.id} />
                    <div>
                      <p className="text-sm font-black text-slate-900">{person.name}</p>
                      <p className="text-xs leading-tight text-slate-500">{person.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-slate-950"><Tag className="h-5 w-5 text-amber-500" /> Tags dominants</h2>
              <div className="flex flex-wrap gap-2">
                {Array.from(new Set(cards.flatMap((card) => card.tags))).slice(0, 34).map((tag) => <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{tag}</span>)}
              </div>
            </div>
          </aside>
        </section>}
      </main>

      <CEPresentationContext.Provider value={selectedCard ? {id:selectedCard.id,meeting,setStatus:status=>updateStatus(selectedCard.id,status)} : null}>
      {selectedCard && isVaria(selectedCard.id) ? <VariaModal card={selectedCard} onClose={()=>setSelectedCard(null)}/> : selectedCard?.id === FINANCE_FD_ID ? (finance.payload ? <FinanceFDPresentation payload={finance.payload} onClose={() => setSelectedCard(null)} /> : null) : selectedCard?.id === 'prisme-master' ? <PrismeImmersiveStory
        card={selectedCard}
        status={statuses[selectedCard.id] || 'a-discuter'}
        onClose={() => setSelectedCard(null)}
        onStatusChange={(status) => updateStatus(selectedCard.id, status)}
      /> : <SubjectModal
        card={selectedCard}
        status={selectedCard ? statuses[selectedCard.id] || 'a-discuter' : 'a-discuter'}
        onClose={() => setSelectedCard(null)}
        onStatusChange={(status) => selectedCard && updateStatus(selectedCard.id, status)}
        onAtelier={() => undefined}
      />}
      </CEPresentationContext.Provider>
    </div>
  );
}
